import React from 'react';
import TripDayExperienceHeader from './TripDayExperienceHeader';
import TripDayExperienceDayBand from './TripDayExperienceDayBand';
import TripDayExperienceGlobalBand from './TripDayExperienceGlobalBand';
import type { RenderAccessAlert, Translate } from './types';

interface TripDayExperienceCardProps {
  experience: any;
  itineraryLabel?: string;
  canDuplicateExperience: boolean;
  onDuplicate: () => void;
  priceLoading: boolean;
  hasAvailabilityWarning: boolean;
  requiredMinPax?: number;
  isCurrencyMismatch: boolean;
  renderAccessAlert?: RenderAccessAlert;
  dayExtrasCount: number;
  dayAvailableCount: number;
  daySelectedExtras: any[];
  globalExtrasCount: number;
  globalAvailableCount: number;
  globalSelectedExtras: any[];
  sumExtrasPrice: (extras: any[]) => number;
  extrasCurrency: (extras: any[]) => string;
  extrasAmountPerPax: (extras: any[]) => number | null;
  renderServiceChips: (extras: any[], scope: any) => React.ReactNode;
  onOpenDayOptions?: () => void;
  onOpenGlobalOptions?: () => void;
  globalServicesSlot?: any;
  t: Translate;
}

// The body of a loaded program day: header, day services band and (on the
// instance's first day) the global services band.
export default function TripDayExperienceCard({
  experience,
  itineraryLabel,
  canDuplicateExperience,
  onDuplicate,
  priceLoading,
  hasAvailabilityWarning,
  requiredMinPax,
  isCurrencyMismatch,
  renderAccessAlert,
  dayExtrasCount,
  dayAvailableCount,
  daySelectedExtras,
  globalExtrasCount,
  globalAvailableCount,
  globalSelectedExtras,
  sumExtrasPrice,
  extrasCurrency,
  extrasAmountPerPax,
  renderServiceChips,
  onOpenDayOptions,
  onOpenGlobalOptions,
  globalServicesSlot,
  t,
}: TripDayExperienceCardProps) {
  return (
    <div className="relative">
      <TripDayExperienceHeader
        experience={experience}
        itineraryLabel={itineraryLabel}
        canDuplicateExperience={canDuplicateExperience}
        onDuplicate={onDuplicate}
        priceLoading={priceLoading}
        hasAvailabilityWarning={hasAvailabilityWarning}
        requiredMinPax={requiredMinPax}
        isCurrencyMismatch={isCurrencyMismatch}
        renderAccessAlert={renderAccessAlert}
        t={t}
      />

      {/* KD-3581 §4 — the day band is never hidden: a day that offers
          nothing says so in its dashed state rather than leaving a
          gap. The global band is not here at all, it closes the frame
          (AC03) — see `TripDayExperienceGlobalBand` below. */}
      <div className="mt-2.5 flex flex-col gap-2.5">
        {!!experience?.dayIndex && (
          <TripDayExperienceDayBand
            dayExtrasCount={dayExtrasCount}
            dayAvailableCount={dayAvailableCount}
            daySelectedExtras={daySelectedExtras}
            sumExtrasPrice={sumExtrasPrice}
            extrasCurrency={extrasCurrency}
            extrasAmountPerPax={extrasAmountPerPax}
            renderServiceChips={renderServiceChips}
            onAction={onOpenDayOptions}
            t={t}
          />
        )}
      </div>

      <TripDayExperienceGlobalBand
        experience={experience}
        globalExtrasCount={globalExtrasCount}
        globalAvailableCount={globalAvailableCount}
        globalSelectedExtras={globalSelectedExtras}
        sumExtrasPrice={sumExtrasPrice}
        extrasCurrency={extrasCurrency}
        extrasAmountPerPax={extrasAmountPerPax}
        renderServiceChips={renderServiceChips}
        onAction={onOpenGlobalOptions}
        globalServicesSlot={globalServicesSlot}
        t={t}
      />
    </div>
  );
}
